// Premium-AI teaser components.
//
// Rendered in place of real AI fields when those fields aren't on the
// finding. Three reasons can trigger the teaser:
//
//   reason="plan"      → user's plan doesn't include AI enrichment.
//                        Show blurred preview + "Upgrade to unlock" CTA.
//   reason="ownership" → user has the plan, asset isn't ownership-
//                        verified yet. Show blurred preview + "Verify
//                        domain ownership" CTA.
//   reason="pending"   → user has the plan AND ownership is verified,
//                        but enrichment hasn't run yet (re-enrich job
//                        is queued, or the original scan's enrichment
//                        was skipped due to a transient issue). NO
//                        upgrade prompt — they already paid. Compact
//                        "processing" notice + manual refresh button.
//                        This case rendered the wrong "Upgrade to Pro"
//                        teaser on pro accounts before the fix.
//
// F12 reveals only the static screenshot — captured once via mock data,
// never real findings. Backend already strips AI fields when gated
// (PR #4) so no real AI payload ever crossed the wire when the
// teaser shows. See CLAUDE.md §4.

import { Button } from "@/components/ui/Button";
import { useDict } from "@/i18n/LocaleProvider";

export type TeaserReason = "plan" | "ownership" | "pending";

interface TeaserShellProps {
  label: string;
  reason: TeaserReason;
  onAction: () => void;
  /** Path under /public — served as a static asset by Vite. */
  previewSrc: string;
}

function TeaserShell({ label, reason, onAction, previewSrc }: TeaserShellProps) {
  const t = useDict().app.ui;
  // Pending state is a different shape: the user has access (paid plan
  // AND verified ownership), the data just isn't ready yet. No upgrade
  // prompt, no locked-content blur — that would be misleading. Compact
  // muted notice with a manual refresh.
  if (reason === "pending") {
    return (
      <div className="mb-4">
        <div className="font-mono text-[10px] text-muted uppercase tracking-[0.08em] mb-2 flex items-center gap-2">
          <span>⏳</span>
          <span>{label}</span>
        </div>
        <div className="p-3 bg-bg2 rounded border border-stroke font-mono text-[11px] text-muted leading-[1.6]">
          {t.teaserPreparing}{" "}
          <button
            type="button"
            onClick={onAction}
            className="text-accent underline cursor-pointer bg-transparent border-none p-0 font-mono text-[11px]"
          >
            {t.teaserRefresh}
          </button>
        </div>
      </div>
    );
  }

  const ctaLabel =
    reason === "ownership"
      ? t.verifyOwnership
      : t.upgradeUnlock;

  return (
    <div className="mb-4">
      {/* Label only — visible, no surrounding box */}
      <div className="font-mono text-[10px] text-accent uppercase tracking-[0.08em] mb-2 flex items-center gap-2">
        <span>🔒</span>
        <span>{label}</span>
        <span className="text-muted normal-case tracking-normal text-[9px]">
          {t.preview}
        </span>
      </div>

      {/* Image at natural size + bare orange CTA centered over it.
          inline-block so the wrapper sizes to the image, not the parent. */}
      <div className="relative inline-block max-w-full" aria-hidden="true">
        <img
          src={previewSrc}
          alt=""
          className="block max-w-full h-auto pointer-events-none select-none rounded"
          style={{ filter: "blur(7px) brightness(0.6)" }}
          draggable={false}
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <Button variant="primary" small onClick={onAction}>
            {ctaLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

interface TeaserProps {
  reason: TeaserReason;
  /** Called when the user clicks the CTA. The parent decides where to send them. */
  onAction: () => void;
}

export function RiskAnalysisTeaser({ reason, onAction }: TeaserProps) {
  return (
    <TeaserShell
      label="Risk Analysis"
      reason={reason}
      onAction={onAction}
      previewSrc="/RiskAnalysis.png"
    />
  );
}

export function AttackPlaybookTeaser({ reason, onAction }: TeaserProps) {
  return (
    <TeaserShell
      label="⚔ Attack Playbook"
      reason={reason}
      onAction={onAction}
      previewSrc="/AtkPlaybook.png"
    />
  );
}
